import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuestionsList from '../../../../Individual/Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;


// Add this component before the main AuditSections component
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Get color based on percentage
  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754'; // success/green
    if (percent >= 75) return '#0d6efd';  // primary/blue
    if (percent >= 50) return '#ffc107';  // warning/yellow
    if (percent >= 25) return '#fd7e14';  // orange
    return '#dc3545';                      // danger/red
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

const FactoryReAuditSections = () => {
  const [sectionNames, setSectionNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [dataTable, setDataTable] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    unansweredQuestions: 0,
    totalPoints: 0,
    maxPossiblePoints: 0,
    percentage: 0
  });
  const [showNext, setShowNext] = useState(false);
  const [currentLevel, setCurrentLevel] = useState('section');
  const [sectionData, setSectionData] = useState(null);
  const [sectionProgress, setSectionProgress] = useState(null);
  const [sectionsProgress, setSectionsProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { checklist } = location.state || {};

  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id || null;

  const notify = (message, type = 'error') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  const calculatePoints = (question, answer) => {
    if (!question.optionPoints) return 0;
    
    try {
      const points = question.optionPoints.split(',').map(p => Number(p.trim()));
      const options = question.answerOptions.split(',').map(opt => opt.trim());
      
      if (Array.isArray(answer)) {
        // For multiple choice questions
        return answer.reduce((total, ans) => {
          const index = options.indexOf(ans);
          return total + (index !== -1 ? points[index] : 0);
        }, 0);
      } else {
        // For single choice questions
        const index = options.indexOf(answer);
        return index !== -1 ? points[index] : 0;
      }
    } catch (error) {
      console.error('Error calculating points:', error);
      return 0;
    }
  };

  // Replace the existing fetchSectionProgress function
  const fetchSectionProgress = async (path = []) => {
    try {
      if (!checklist?.id) {
        throw new Error('Missing checklist ID');
      }

      const pathString = Array.isArray(path) ? path.join('|') : path;
      const encodedPath = encodeURIComponent(pathString);

      const response = await fetch(
        `${BASE_URL}/get-factory-reaudit-section-progress/${checklist.id}/${encodedPath}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch progress');
      }
        
      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.error || 'Failed to load progress data');
      }

      setSectionProgress({
        totalQuestions: data.totalQuestions || 0,
        answeredQuestions: data.answeredQuestions || 0,
        progressPercentage: data.progressPercentage || 0,
        level: data.level || 'section'
      });

      return data;
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to load progress data', 'error');
      throw error; // Re-throw to handle in calling function
    }
  };

  // Update fetchSections function
  const fetchSections = async () => {
    if (!checklist?.id) {
      notify("Missing checklist information");
      setLoading(false);
      return;
    }

    try {
      // Get re-audit details including sections
      const response = await fetch(
        `${BASE_URL}/get-factory-reaudit-details/${checklist.id}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }

      const data = await response.json();
      if (!data.sections) {
        throw new Error('No sections data in response');
      }

      const parsedSections = JSON.parse(data.sections);
      setSectionNames(parsedSections.map(s => s.name));
    } catch (error) {
      console.error('Fetch error:', error);
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [checklist]);

  // Update fetchAllSectionsProgress function
  const fetchAllSectionsProgress = async () => {
    if (!checklist?.id || !sectionNames?.length) {
      console.error('Missing required data:', { 
        checklistId: checklist?.id, 
        sections: sectionNames 
      });
      return;
    }

    setLoading(true);
    const progressMap = {};

    try {
      // Process each section sequentially to avoid race conditions
      for (const sectionName of sectionNames) {
        const pathString = sectionName;
        
        try {
          // Fetch progress for current path
          const progressResponse = await fetch(
            `${BASE_URL}/get-factory-reaudit-section-progress/${checklist.id}/${pathString}`
          );
          
          if (!progressResponse.ok) {
            throw new Error(`Failed to fetch progress for ${pathString}`);
          }

          const progressData = await progressResponse.json();
          progressMap[pathString] = {
            progressPercentage: progressData.progressPercentage || 0,
            answeredQuestions: progressData.answeredQuestions || 0,
            totalQuestions: progressData.totalQuestions || 0,
            level: progressData.level || 'section'
          };

          // Only fetch nested details if we got valid progress data
          const detailsResponse = await fetch(
            `${BASE_URL}/get-factory-reaudit-nested-details/${checklist.id}/${pathString}`
          );
          
          if (!detailsResponse.ok) {
            throw new Error(`Failed to fetch details for ${pathString}`);
          }

          const sectionData = await detailsResponse.json();

          // Process subsections if they exist
          if (sectionData?.subsections?.length > 0) {
            for (const subsection of sectionData.subsections) {
              const subPathString = `${pathString}|${subsection.name}`;
              const subProgressResponse = await fetch(
                `${BASE_URL}/get-factory-reaudit-section-progress/${checklist.id}/${subPathString}`
              );

              if (subProgressResponse.ok) {
                const subProgressData = await subProgressResponse.json();
                progressMap[subPathString] = {
                  progressPercentage: subProgressData.progressPercentage || 0,
                  answeredQuestions: subProgressData.answeredQuestions || 0,
                  totalQuestions: subProgressData.totalQuestions || 0,
                  level: subProgressData.level || 'subsection'
                };
              }
            }
          }
        } catch (sectionError) {
          console.error(`Error processing section ${pathString}:`, sectionError);
          // Continue with other sections even if one fails
        }
      }

      setSectionsProgress(progressMap);
    } catch (error) {
      console.error('Error fetching progress:', error);
      notify('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  // Update the useEffect hook
  useEffect(() => {
    if (sectionNames?.length > 0 && checklist?.id) {
      fetchAllSectionsProgress();
    }
  }, [sectionNames, checklist?.id]);

  // Add helper function to validate section data
  const validateSectionData = (data) => {
    return {
      progressPercentage: data?.progressPercentage || 0,
      answeredQuestions: data?.answeredQuestions || 0,
      totalQuestions: data?.totalQuestions || 0,
      level: data?.level || 'section'
    };
  };

  const fetchSubsectionProgress = async (subsectionPath) => {
    try {
      const pathString = Array.isArray(subsectionPath) 
        ? subsectionPath.join('|')
        : subsectionPath;

      const progressResponse = await fetch(
        `${BASE_URL}/get-factory-reaudit-section-progress/${checklist.id}/${pathString}`
      );

      if (!progressResponse.ok) {
        throw new Error(`Failed to fetch progress for path: ${pathString}`);
      }

      const progressData = await progressResponse.json();
      
      setSubsectionProgress(prev => ({
        ...prev,
        [pathString]: progressData
      }));

      // Get nested details
      const detailsResponse = await fetch(
        `${BASE_URL}/get-factory-reaudit-nested-details/${checklist.id}/${pathString}`
      );

      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        
        if (details.subsections?.length > 0) {
          await Promise.all(details.subsections.map(async (nestedSubsection) => {
            const nestedPath = Array.isArray(subsectionPath)
              ? [...subsectionPath, nestedSubsection.name]
              : `${pathString}|${nestedSubsection.name}`;
              
            try {
              const nestedProgressResponse = await fetch(
                `${BASE_URL}/get-factory-reaudit-section-progress/${checklist.id}/${Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath}`
              );

              if (nestedProgressResponse.ok) {
                const nestedProgressData = await nestedProgressResponse.json();
                setSubsectionProgress(prev => ({
                  ...prev,
                  [Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath]: nestedProgressData
                }));
              }
            } catch (nestedError) {
              console.error(`Error fetching nested progress for ${nestedSubsection.name}:`, nestedError);
            }
          }));
        }
      }

      return progressData;
    } catch (error) {
      console.error('Error fetching subsection progress:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchProgress = async () => {
      if (Array.isArray(subsections) && subsections.length > 0 && currentPath.length > 0) {
        try {
          // Fetch progress for all subsections in parallel
          await Promise.all(subsections.map(async (subsection) => {
            if (subsection?.name) {
              const subsectionPath = [...currentPath, subsection.name];
              await fetchSubsectionProgress(subsectionPath);
            }
          }));
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }
    };

    fetchProgress();
  }, [subsections, currentPath, checklist?.id]);

  // Add data validation helper
  const validateSubsectionData = (data) => {
    return {
      name: data?.name || '',
      questions: Array.isArray(data?.questions) ? data.questions : [],
      subsections: Array.isArray(data?.subsections) ? data.subsections : []
    };
  };

  // Update loadSavedAnswers function
  const loadSavedAnswers = async (sectionName) => {
    if (!checklist?.id || !checklist?.branch_id) {
      console.error('Missing required data for loading answers');
      return;
    }
  
    try {
      const response = await fetch(
        `${BASE_URL}/get-factory-reaudit-responses/${checklist.id}/${checklist.branch_id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch saved answers');
      }
      
      const data = await response.json();
      
      // Transform and filter answers
      const relevantAnswers = data.reduce((acc, curr) => {
        if (!sectionName || curr.section === sectionName) {
          // Handle media files structure
          const mediaFiles = curr.mediaFiles?.map(file => ({
            filename: file.filename,
            name: file.name,
            isNew: false,
            url: file.url
          })) || [];
  
          acc[curr.questionId] = {
            questionId: curr.questionId,
            section: curr.section,
            subsection: curr.subsection,
            subsubsection: curr.subsubsection,
            answer: curr.answer || '',
            remarks: curr.remarks || '',
            points: curr.points || 0,
            cap: curr.cap || 'No',
            mediaFiles: mediaFiles,
            mediaFile: curr.mediaFile || [], // Keep original format for compatibility
            questionText: curr.questionText,
            answerType: curr.answerType,
            answerOptions: curr.answerOptions || [],
            optionPoints: curr.optionPoints || []
          };
        }
        return acc;
      }, {});
  
      setAnswers(prev => ({
        ...prev,
        ...relevantAnswers
      }));
      
      // Update questions with media files
      if (questions.length > 0) {
        setQuestions(prev => prev.map(q => ({
          ...q,
          mediaFiles: relevantAnswers[q.id]?.mediaFiles || [],
          mediaFile: relevantAnswers[q.id]?.mediaFile || null,
          answer: relevantAnswers[q.id]?.answer || '',
          remarks: relevantAnswers[q.id]?.remarks || '',
          points: relevantAnswers[q.id]?.points || 0,
          cap: relevantAnswers[q.id]?.cap || 'No'
        })));
      }
      
      // Update progress tracking
      const answeredCount = Object.values(relevantAnswers).filter(a => a.answer).length;
      
      setDataTable(prev => ({
        ...prev,
        answeredQuestions: answeredCount,
        totalQuestions: questions.length,
        unansweredQuestions: questions.length - answeredCount
      }));
  
    } catch (error) {
      console.error('Error loading saved answers:', error);
      notify('Failed to load saved answers');
    }
  };

  const handleSectionClick = async (sectionName, index) => {
    setSelectedSection(sectionName);
    setLoading(true);
    setCurrentPath([sectionName]);
    setShowNext(false);
    setCurrentLevel('section');
  
    try {
      const response = await fetch(
        `${BASE_URL}/get-factory-reaudit-section-details/${checklist.id}/${encodeURIComponent(sectionName)}`
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch section details');
      }
  
      const data = await response.json();
  
      setSectionData(data);
      
      if (data.type === 'questions' || data.type === 'both') {
        // Handle questions
        const questionsData = data.questions || [];
        
        const formattedQuestions = questionsData.map(q => ({
          id: q.id,
          questionText: q.questionText,
          answerType: q.answerType,
          answerOptions: q.answerOptions,
          optionPoints: q.optionPoints,
          answer: q.answer || '',
          remarks: q.remarks || '',
          points: q.points || 0,
          cap: q.cap || 'No',
          mediaFile: q.mediaFile || null
        }));
  
        setQuestions(formattedQuestions);
        setSubsections(data.subsections || []);
        setShowNext(data.subsections?.length > 0);
      } else if (data.type === 'subsections') {
        // Handle subsections only
        setQuestions([]);
        const subsectionsData = data.data || [];
        setSubsections(subsectionsData);
        setShowNext(false);
      } else {
        setQuestions([]);
        setSubsections([]);
        notify('No content found for this section', 'info');
      }
  
      // Update the section data for navigation
      if (data.type === 'subsections' && data.data) {
        setSectionData({
          ...data,
          subsections: data.data
        });
      }
  
      await loadSavedAnswers(sectionName);
      await fetchSectionProgress([sectionName]);
  
    } catch (error) {
      console.error('Error:', error);
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Update handleItemClick function to properly format question data
  const handleItemClick = async (itemName, itemType, parentPath = []) => {
    if (!itemName || !checklist?.id) {
      notify('Missing required data');
      return;
    }
  
    setLoading(true);
    const newPath = [...parentPath, itemName];
    setCurrentPath(newPath);
    setShowNext(false);
    
    try {
      const pathParam = newPath.join('|');
  
      const response = await fetch(
        `${BASE_URL}/get-factory-reaudit-nested-details/${checklist.id}/${encodeURIComponent(pathParam)}`
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }
  
      const data = await response.json();
  
      setSectionData(data);
      
      const nextLevel = newPath.length === 1 ? 'subsection' : 
                       newPath.length === 2 ? 'subsubsection' : 
                       'final';
      setCurrentLevel(nextLevel);
  
      if (data.type === 'questions' || data.type === 'both') {
        // Ensure proper data formatting for questions
        const formattedQuestions = (data.questions || []).map(q => ({
          id: q.id || '',
          questionText: String(q.questionText || q.question_text || ''),
          answerType: String(q.answerType || q.answer_type || 'FreeText'),
          answerOptions: typeof q.answerOptions === 'string' ? 
            JSON.parse(q.answerOptions || '[]') : 
            Array.isArray(q.answerOptions) ? q.answerOptions : [],
          optionPoints: typeof q.optionPoints === 'string' ? 
            JSON.parse(q.optionPoints || '[]') : 
            Array.isArray(q.optionPoints) ? q.optionPoints : [],
          answer: String(q.answer || ''),
          remarks: String(q.remarks || ''),
          points: Number(q.points || 0),
          cap: String(q.cap || 'No'),
          mediaFile: q.mediaFile || q.media_file || null
        }));
  
        setQuestions(formattedQuestions);
        
        if (data.subsections?.length > 0) {
          const formattedSubsections = data.subsections.map(sub => ({
            ...sub,
            questions: Array.isArray(sub.questions) ? sub.questions : [],
            subsections: Array.isArray(sub.subsections) ? sub.subsections : []
          }));
          setSubsections(formattedSubsections);
          setShowNext(true);
        } else {
          setSubsections([]);
          setShowNext(false);
        }
      } else if (data.type === 'subsections') {
        setQuestions([]);
        const formattedSubsections = (data.subsections || []).map(sub => ({
          ...sub,
          questions: Array.isArray(sub.questions) ? sub.questions : [],
          subsections: Array.isArray(sub.subsections) ? sub.subsections : []
        }));
        setSubsections(formattedSubsections);
        setShowNext(false);
      } else {
        setQuestions([]);
        setSubsections([]);
        notify('No content found', 'info');
      }
  
      await loadSavedAnswers(newPath[0]);
      await fetchSectionProgress(newPath);
  
    } catch (error) {
      console.error('Error:', error);
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const navigateToPath = (index) => {
    // Navigate to a specific point in the path
    const newPath = currentPath.slice(0, index + 1);
    handleItemClick(newPath[newPath.length - 1], 'section', newPath.slice(0, -1));
  };

  const handleAnswerChange = (questionId, changes) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...changes,
        section: currentPath[0] // Always include top-level section
      }
    }));

    // Update progress tracking
    setDataTable(prev => {
      const answeredCount = Object.values(answers).filter(a => a.answer).length;
      return {
        ...prev,
        answeredQuestions: answeredCount,
        unansweredQuestions: prev.totalQuestions - answeredCount
      };
    });
  };

  const toastConfig = {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  // Update the handleSubmit function 
  const handleSubmit = async (shouldNavigate = false) => {
    try {
      const formData = new FormData();
      const answersToSubmit = [];

      // Validate required data
      if (!checklist?.id || !auditorId || !checklist?.branch_id) {
        notify('Missing checklist, auditor or branch information');
        return;
      }

      Object.entries(answers).forEach(([questionId, answerData]) => {
        if (!answerData?.answer?.trim()) return;

        const answerPayload = {
          questionId,
          section: currentPath[0],
          subsection: currentPath[1],
          subsubsection: currentPath[2],
          answer: answerData.answer,
          remarks: answerData.remarks || '',
          points: answerData.points || 0,
          cap: answerData.cap,
          mediaFileNames: [] // Initialize array for media files
        };

        // Handle media files
        if (answerData.mediaFiles?.length > 0) {
          answerData.mediaFiles.forEach(file => {
            if (file.file instanceof File) {
              // For new files
              formData.append('media', file.file);
              answerPayload.mediaFileNames.push(file.file.name);
            } else if (file.filename) {
              // For existing files
              answerPayload.mediaFileNames.push(file.filename);
            }
          });
        }

        // Add deleted files if any
        if (answerData.deletedFiles?.length > 0) {
          answerPayload.deletedFiles = answerData.deletedFiles;
        }

        answersToSubmit.push(answerPayload);
      });

      // Add data to formData
      formData.append('data', JSON.stringify({
        checklistId: checklist.id,
        answers: answersToSubmit,
        auditorId: auditorId,
        branchId: checklist.branch_id
      }));

      const response = await fetch(`${BASE_URL}/save-factory-reaudit-answers`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save answers');
      }

      // Update media files in state
      if (result.savedFiles?.length) {
        setAnswers(prev => {
          const updated = { ...prev };
          result.savedFiles.forEach(savedFile => {
            Object.keys(updated).forEach(questionId => {
              if (updated[questionId].mediaFiles) {
                const updatedFiles = updated[questionId].mediaFiles.map(file => {
                  if (file.file instanceof File && file.file.name === savedFile.originalName) {
                    return {
                      filename: savedFile.filename,
                      name: savedFile.originalName,
                      isNew: false,
                      url: `${BASE_URL}/uploads/audit-media/${savedFile.filename}`
                    };
                  }
                  return file;
                });
                updated[questionId].mediaFiles = updatedFiles;
                // Update mediaFile field for backwards compatibility
                updated[questionId].mediaFile = JSON.stringify(updatedFiles.map(f => f.filename));
              }
            });
          });
          return updated;
        });

        // Refresh questions list to show updated media
        if (questions.length > 0) {
          setQuestions(prev => prev.map(q => ({
            ...q,
            mediaFile: answers[q.id]?.mediaFile || null,
            mediaFiles: answers[q.id]?.mediaFiles || []
          })));
        }
      }

      // Refresh progress after save
      await fetchSectionProgress(currentPath);

      // Show success toast with conditional navigation
      if (shouldNavigate) {
        toast.success("Answers saved successfully!", {
          ...toastConfig,
          onClose: () => navigate('/FactoryReAuditDetails', { 
            state: { 
              checklist,
              from: 'sections',
              sectionPath: currentPath
            }
          })
        });
      } else {
        toast.success("Progress saved successfully!", toastConfig);
      }

    } catch (error) {
      console.error('Error saving answers:', error);
      notify(error.message);
    }
  };

  const handleSaveProgress = () => {
    handleSubmit(false); // Save without navigation
  };

  const handleSaveAndContinue = () => {
    handleSubmit(true); // Save with navigation
  };

  const resetNavigation = () => {
    setSelectedSection(null);
    setSubsections([]);
    setQuestions([]);
    setCurrentPath([]);
  };

  const handleNext = () => {
    if (sectionData?.subsections?.length > 0) {
      setQuestions([]);
      setSubsections(sectionData.subsections);
      setShowNext(false);
      setCurrentLevel(prevLevel => {
        switch (prevLevel) {
          case 'section':
            return 'subsection';
          case 'subsection':
            return 'subsubsection';
          default:
            return prevLevel;
        }
      });
    }
  };

  // Add renderSubsectionCard helper function
  const renderSubsectionCard = (subsection, parentPath) => {
    const validatedData = validateSubsectionData(subsection);
    const fullPath = [...parentPath, validatedData.name].join('|');
    const progress = subsectionProgress[fullPath] || {
      progressPercentage: 0,
      answeredQuestions: 0,
      totalQuestions: validatedData.questions?.length || 0
    };
  
    return (
      <Card 
        className="h-100 shadow-sm hover-shadow"
        onClick={() => handleItemClick(validatedData.name, 'subsection', parentPath)}
        role="button"
      >
        <Card.Body className="d-flex flex-column">
          <div className="text-center mb-3">
            <CircularProgress 
              percentage={progress.progressPercentage} 
              size={80}
              showLabel={true}
            />
          </div>
          <div className="text-center mb-3">
            <h5 className="mb-1">{validatedData.name}</h5>
            <small className="text-muted d-block">
              {progress.totalQuestions} Questions
            </small>
          </div>
          <div className="mt-auto">
            <div className="d-flex justify-content-center gap-3 mb-2">
              <div className="text-success">
                <small>{progress.answeredQuestions} Answered</small>
              </div>
              <div className="text-danger">
                <small>
                  {progress.totalQuestions - progress.answeredQuestions} Remaining
                </small>
              </div>
            </div>
            <div className="text-center">
              <small className={`badge bg-${
                progress.progressPercentage === 100 ? 'success' :
                progress.progressPercentage > 0 ? 'warning' : 'danger'
              }`}>
                {progress.progressPercentage === 100 ? 'Completed' :
                progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
              </small>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

 // Replace the current loading spinner with the enhanced version:
if (loading) {
  return (
    <Container fluid className="py-5">
      <div className="text-center py-5">
        <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-primary fw-bold mb-1">Loading Re-Audit Sections</p>
        <p className="text-muted">Please wait while we fetch your re-audit section data...</p>
      </div>
    </Container>
  );
}

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      
      <Row className="mb-4">
        <Col>
          <h3 className="mb-2">{checklist.checklist_name}</h3>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  resetNavigation();
                }}>Sections</a>
              </li>
              
              {currentPath.map((pathItem, index) => (
                <li 
                  key={index} 
                  className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                >
                  {index === currentPath.length - 1 ? (
                    <span>{pathItem}</span>
                  ) : (
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      navigateToPath(index);
                    }}>{pathItem}</a>
                  )}
                </li>
              ))}
              
              {questions.length > 0 && currentPath.length > 0 && (
                <li className="breadcrumb-item active">Questions</li>
              )}
            </ol>
          </nav>
        </Col>
      </Row>


      {currentPath.length === 0 ? (
        <Row xs={1} md={2} lg={3} className="g-4">
          {sectionNames.map((sectionName, index) => {
            const progress = validateSectionData(sectionsProgress[sectionName]);

            return (
              <Col key={index}>
                <Card 
                  className="h-100 shadow-sm hover-shadow"
                  onClick={() => handleSectionClick(sectionName, index)}
                  role="button"
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="text-center mb-3">
                      <CircularProgress 
                        percentage={progress.progressPercentage}
                        size={80}
                        showLabel={true}
                      />
                    </div>
                    <div className="text-center mb-3">
                      <h5 className="mb-1">{sectionName}</h5>
                      <small className="text-muted d-block">
                        {progress.totalQuestions} Questions
                      </small>
                    </div>
                    <div className="mt-auto">
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="text-success">
                          <small>{progress.answeredQuestions} Answered</small>
                        </div>
                        <div className="text-danger">
                          <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <small className={`badge bg-${
                          progress.progressPercentage === 100 ? 'success' :
                          progress.progressPercentage > 0 ? 'warning' : 'danger'
                        }`}>
                          {progress.progressPercentage === 100 ? 'Completed' :
                          progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <>
          {questions.length > 0 && (
            <>
              <QuestionsList
                questions={questions}
                answers={answers}
                handleAnswerChange={handleAnswerChange}
                setAnswers={setAnswers}
                selectedSection={currentPath[currentPath.length - 1]}
                handleSubmit={!showNext ? handleSaveAndContinue : undefined}
                checklist={checklist}
                auditorId={auditorId}
              />
              
              {showNext && (
                <div className="text-center mt-4">
                  <Button 
                    variant="primary" 
                    onClick={handleNext}
                    className="d-flex align-items-center mx-auto"
                  >
                    <span className="me-2">
                      Next: View {currentLevel === 'section' ? 'Subsections' : 'Sub-subsections'}
                    </span>
                    <span className="badge bg-light text-primary">
                      {sectionData?.subsections?.length || 0}
                    </span>
                  </Button>
                </div>
              )}
            </>
          )}

          {subsections.length > 0 && !questions.length && (
            <Row xs={1} md={2} lg={3} className="g-4">
              {subsections.map((subsection, index) => (
                <Col key={index}>
                  {renderSubsectionCard(subsection, currentPath)}
                </Col>
              ))}
            </Row>
          )}

          {!questions.length && !subsections.length && (
            <div className="text-center py-5">
              <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
              <h4 className="text-muted">No content found</h4>
              <Button 
                variant="outline-primary" 
                className="mt-3"
                onClick={resetNavigation}
              >
                Return to Sections
              </Button>
            </div>
          )}

          {(questions.length > 0 || subsections.length > 0) && (
            <div className="navigation-buttons d-flex justify-content-between mt-4">
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  if (currentPath.length === 1) {
                    resetNavigation();
                  } else {
                    navigateToPath(currentPath.length - 2);
                  }
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>

              <div className="d-flex gap-2">
                              {questions.length > 0 && (
                                <Button 
                                  variant="primary"
                                  onClick={handleSaveProgress}
                                  disabled={isSaving}
                                  className="d-flex align-items-center"
                                >
                                  {isSaving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-save me-2"></i>
                                      Save Progress
                                    </>
                                  )}
                                </Button>
                              )}
              
                              {showNext && (
                                <Button 
                                  variant="info"
                                  onClick={handleNext}
                                  className="d-flex align-items-center"
                                >
                                  Next
                                  <i className="fas fa-arrow-right ms-2"></i>
                                </Button>
                              )}
              
                              {!showNext && questions.length > 0 && (
                                <Button 
                                  variant="success"
                                  onClick={handleSaveAndContinue}
                                  disabled={isSaving}
                                  className="d-flex align-items-center"
                                >
                                  {isSaving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      Save & Continue
                                      <i className="fas fa-check ms-2"></i>
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
            </div>
          )}
        </>
      )}

      <style>
        {`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
          }
          .department-icon, .subsection-icon, .subsubsection-icon {
            width: 50px;
            height: 50px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(13, 110, 253, 0.1);
          }
          .subsection-icon, .subsubsection-icon {
            background-color: rgba(23, 162, 184, 0.1);
          }
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }
          .circular-progress {
            position: relative;
            margin-left: 1rem;
          }
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          .card:hover .circular-progress circle.progress-bar {
            filter: brightness(0.9);
          }
          .border-top {
            border-top: 1px solid rgba(0,0,0,.125) !important;
          }
          .card {
            transition: all 0.3s ease;
          }
          .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
          }
          .progress-text-large {
            font-size: 24px;
            font-weight: 700;
            transition: fill 0.35s;
          }
          .progress-text-small {
            font-size: 12px;
            fill: #6c757d;
          }
          .badge {
            font-weight: 500;
            padding: 0.5em 1em;
          }
          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          @media (max-width: 576px) {
            .navigation-buttons {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              border-radius: 0;
              border-top: 1px solid rgba(0,0,0,0.1);
            }
          }

          .spinner-grow {
            animation-duration: 1.5s;
          }
          
          .text-primary.fw-bold {
            font-size: 1.2rem;
          }
          
          /* Fadeout animation for when loading completes */
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          
          .loading-fadeout {
            animation: fadeOut 0.5s ease-out forwards;
          }
        `}
      </style>
    </Container>
  );
};

export default FactoryReAuditSections;